// Mutations de escrita da tela de Provedores do WL — associação
// canal→provider, credenciais Zenvia (write-only), journeys e teste
// de conexão — com UPDATE OTIMISTA + rollback + toast.
//
// Associação canal→provider já é WRITE REAL (backend persiste em
// configuration_channel_provider.enable): o otimismo dá resposta
// imediata e o `invalidateOnSettled` refaz o fetch para consolidar o
// estado do servidor. As mutations de Zenvia (credenciais/journeys)
// SEGUEM mockadas no gateway — por isso NÃO invalidam: um refetch
// traria o read real (journeys vazios nesta onda) e desfaria o
// otimismo. Zenvia real é uma onda própria.
import { useTranslation } from "react-i18next"

import { useToast } from "@/hooks/use-toast"
import { useMutation, useQueryClient } from "@/libs"
import {
  deleteNotificationCenterZenviaJourney,
  postNotificationCenterZenviaJourney,
  postNotificationCenterZenviaTest,
  putNotificationCenterChannelProvider,
  putNotificationCenterZenviaCredentials,
  putNotificationCenterZenviaJourney,
} from "@/services/notification-center"
import type {
  NotificationChannelProviderWritePayloadType,
  NotificationSearchChannelType,
  NotificationWhiteLabelProvidersResponseType,
  NotificationZenviaCredentialsPayloadType,
  NotificationZenviaJourneyType,
  NotificationZenviaJourneyWritePayloadType,
  NotificationZenviaTestResultType,
} from "@/types/notification-center"

import { getWhiteLabelProvidersQueryKey } from "./hooks"

const providersPrefix =
  "pages.notificationCenter.providersPage"

// Contexto de rollback compartilhado pelas mutations.
type ProvidersSnapshotContext = {
  previous?: NotificationWhiteLabelProvidersResponseType
}

// Aplica uma transformação imutável à config Zenvia do cache.
const patchZenviaInCache = (
  cache: NotificationWhiteLabelProvidersResponseType,
  patch: (
    zenvia: NotificationWhiteLabelProvidersResponseType["zenvia"]
  ) => NotificationWhiteLabelProvidersResponseType["zenvia"]
): NotificationWhiteLabelProvidersResponseType => ({
  ...cache,
  zenvia: patch(cache.zenvia),
})

// Fábrica dos callbacks otimistas (mesmo padrão da Configuração):
// snapshot no onMutate, rollback + toast destrutivo no onError,
// toast de sucesso no onSuccess.
const useOptimisticProvidersWrite = <TVariables>(
  whiteLabelId: string,
  applyOptimistic: (
    cache: NotificationWhiteLabelProvidersResponseType,
    variables: TVariables
  ) => NotificationWhiteLabelProvidersResponseType,
  mutationFn: (variables: TVariables) => Promise<unknown>,
  // Refaz o fetch no fim (sucesso ou erro) para consolidar o estado do
  // servidor. Só para writes REAIS (associação canal→provider). Deixe
  // false para writes ainda mockados (Zenvia), senão o refetch desfaz o
  // otimismo.
  invalidateOnSettled = false
) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const queryKey =
    getWhiteLabelProvidersQueryKey(whiteLabelId)

  return useMutation<
    unknown,
    Error,
    TVariables,
    ProvidersSnapshotContext
  >({
    mutationFn,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey })
      const previous =
        queryClient.getQueryData<NotificationWhiteLabelProvidersResponseType>(
          queryKey
        )
      if (previous) {
        queryClient.setQueryData(
          queryKey,
          applyOptimistic(previous, variables)
        )
      }
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
      toast({
        title: t(`${providersPrefix}.writeError`),
        variant: "destructive",
      })
    },
    onSuccess: () => {
      toast({
        title: t(`${providersPrefix}.writeSuccess`),
      })
    },
    onSettled: invalidateOnSettled
      ? () => {
          queryClient.invalidateQueries({ queryKey })
        }
      : undefined,
  })
}

// PUT …/providers/:channel — associa provider e liga/desliga o canal.
export const useUpdateChannelProvider = (
  whiteLabelId: string
) =>
  useOptimisticProvidersWrite<{
    channel: NotificationSearchChannelType
    payload: NotificationChannelProviderWritePayloadType
  }>(
    whiteLabelId,
    (cache, { channel, payload }) => ({
      ...cache,
      channels: cache.channels.map((channelProvider) =>
        channelProvider.channel === channel
          ? { ...channelProvider, ...payload }
          : channelProvider
      ),
    }),
    ({ channel, payload }) =>
      putNotificationCenterChannelProvider(
        whiteLabelId,
        channel,
        payload
      ),
    // write real: consolida com o servidor após o toggle
    true
  )

// PUT …/providers/zenvia/credentials — otimista em tenantId/clientId
// e clientSecretSet=true; o secret NUNCA entra no cache.
export const useUpdateZenviaCredentials = (
  whiteLabelId: string
) =>
  useOptimisticProvidersWrite<NotificationZenviaCredentialsPayloadType>(
    whiteLabelId,
    (cache, payload) =>
      patchZenviaInCache(cache, (zenvia) => ({
        ...zenvia,
        tenantId: payload.tenantId,
        clientId: payload.clientId,
        clientSecretSet: true,
      })),
    (payload) =>
      putNotificationCenterZenviaCredentials(
        whiteLabelId,
        payload
      )
  )

// Shape de exibição do cache otimista de journey (eventLabel vem do
// catálogo de constantes no chamador — o ack estático não devolve).
export type ZenviaJourneyWriteVariables = {
  payload: NotificationZenviaJourneyWritePayloadType
  eventLabel: string
}

// POST …/providers/zenvia/journeys — cria o mapeamento (id local
// temporário até a onda 4 devolver o id real) e remove o evento da
// lista de não-mapeados.
export const useCreateZenviaJourney = (
  whiteLabelId: string
) =>
  useOptimisticProvidersWrite<ZenviaJourneyWriteVariables>(
    whiteLabelId,
    (cache, { payload, eventLabel }) =>
      patchZenviaInCache(cache, (zenvia) => ({
        ...zenvia,
        journeys: [
          ...zenvia.journeys,
          {
            id: `ZPJ-local-${Date.now()}`,
            event: payload.event,
            eventLabel,
            channel: payload.channel,
            journeyId: payload.journeyId,
            versionId: payload.versionId ?? null,
          } satisfies NotificationZenviaJourneyType,
        ],
        unmappedActiveEvents:
          zenvia.unmappedActiveEvents.filter(
            (unmapped) =>
              !(
                unmapped.event === payload.event &&
                unmapped.channel === payload.channel
              )
          ),
      })),
    ({ payload }) =>
      postNotificationCenterZenviaJourney(
        whiteLabelId,
        payload
      )
  )

// PUT …/providers/zenvia/journeys/:id — atualiza o mapeamento.
export const useUpdateZenviaJourney = (
  whiteLabelId: string
) =>
  useOptimisticProvidersWrite<
    ZenviaJourneyWriteVariables & {
      journeyRecordId: string
    }
  >(
    whiteLabelId,
    (cache, { journeyRecordId, payload, eventLabel }) =>
      patchZenviaInCache(cache, (zenvia) => ({
        ...zenvia,
        journeys: zenvia.journeys.map((journey) =>
          journey.id === journeyRecordId
            ? {
                ...journey,
                event: payload.event,
                eventLabel,
                channel: payload.channel,
                journeyId: payload.journeyId,
                versionId: payload.versionId ?? null,
              }
            : journey
        ),
      })),
    ({ journeyRecordId, payload }) =>
      putNotificationCenterZenviaJourney(
        whiteLabelId,
        journeyRecordId,
        payload
      )
  )

// DELETE …/providers/zenvia/journeys/:id — remove o mapeamento.
// (O retorno do evento à lista de não-mapeados depende do estado do
// evento no WL — só o backend sabe; virá no refetch da onda 4.)
export const useDeleteZenviaJourney = (
  whiteLabelId: string
) =>
  useOptimisticProvidersWrite<{ journeyRecordId: string }>(
    whiteLabelId,
    (cache, { journeyRecordId }) =>
      patchZenviaInCache(cache, (zenvia) => ({
        ...zenvia,
        journeys: zenvia.journeys.filter(
          (journey) => journey.id !== journeyRecordId
        ),
      })),
    ({ journeyRecordId }) =>
      deleteNotificationCenterZenviaJourney(
        whiteLabelId,
        journeyRecordId
      )
  )

// POST …/providers/zenvia/test — teste de conexão (sem otimismo:
// não muda o cache). Toast com a latência no sucesso.
export const useTestZenviaConnection = (
  whiteLabelId: string
) => {
  const { t } = useTranslation()
  const { toast } = useToast()

  return useMutation<
    NotificationZenviaTestResultType,
    Error,
    void
  >({
    mutationFn: () =>
      postNotificationCenterZenviaTest(whiteLabelId),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: t(
            `${providersPrefix}.zenvia.testSuccess`,
            { latencyMs: result.latencyMs }
          ),
        })
      } else {
        toast({
          title: t(`${providersPrefix}.zenvia.testFailure`),
          variant: "destructive",
        })
      }
    },
    onError: () => {
      toast({
        title: t(`${providersPrefix}.zenvia.testError`),
        variant: "destructive",
      })
    },
  })
}
